import { previousMonday } from 'date-fns';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleListItem } from '../../../../api/model';
import * as sessionStrageUtils from '../../../../utils/sessionStorage';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import SlideToggle from '../../../modules/SlideToggle';
import { getFormattedDate } from '../SchedulelistCommon';
import GanttChart from './GanttChart';
import GanttChartAreaHeadder from './GanttChartAreaHeadder';
import { ChartData, GanttChartProcess, ProcessData } from './GanttChartData';
import GanttChartTheadder from './GanttChartTheadder';

/* グラフ種別 */
export type ChartType = 'Month' | 'Week' | 'Day';

interface Props {
  openGanttChart: boolean;
  setShowChildList: Dispatch<SetStateAction<string[]>>;
  changeChildList: (targetPsVersionId: string) => void;
  isShowChild: (targetPsVersionId: string) => boolean;
  data: ScheduleListItem[];
  allData: ScheduleListItem[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * ガントチャートエリア描画
 */
function GanttChartArea({
  openGanttChart,
  setShowChildList,
  changeChildList,
  isShowChild,
  data,
  allData,
  isLoading,
  isError,
}: Props) {
  const { t } = useTranslation();

  // ガントチャートグラフ種別（デフォルト：Month）
  const [chartType, setChartType] = useState<ChartType>(
    sessionStrageUtils.get<ChartType>('ScheduleList.selectedChartType', 'Month')
  );

  // ガントチャートヘッダー部日付一覧取得
  const { startDay, endDay } = getDateList(data);

  // ガントチャート すべての子要素の資料管理No.をリスト化する
  const childProductionIdList = getChildProductionIdList(allData);

  // 対象期間リスト作成
  const dateList: Date[] = [];
  // 期間中の日付を中抜けなしで対象期間リストにpushしていく
  for (let d = startDay; d <= endDay; d.setDate(d.getDate() + 1)) {
    dateList.push(new Date(d));
  }

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return null;
  }

  // データが空の場合
  if (data === undefined || data == null) {
    return <NoDataComponent />;
  }
  // APIレスポンスをクライアント側で操作しやすいオブジェクトに変換する
  const chartDataList = ConvertGanttChart(dateList, data, chartType, t);
  return (
    <SlideToggle open={openGanttChart}>
      <div className="wfr documentlist pt20">
        <GanttChartAreaHeadder
          chartType={chartType}
          setChartType={setChartType}
          setShowChildList={setShowChildList}
          childProductionIdList={childProductionIdList}
        />
        <div className="scroll">
          <div className="table23 ganttchart-table">
            <GanttChartTheadder chartType={chartType} dateList={dateList} />
            <GanttChart ganttChartList={chartDataList} isShowChild={isShowChild} changeChildList={changeChildList} />
          </div>
        </div>
      </div>
    </SlideToggle>
  );
}

/**
 * APIのレスポンスを解析してガントチャートとして扱いやすい形に変換する
 * @returns
 */
function ConvertGanttChart(
  dateList: Date[],
  scheduleList: ScheduleListItem[],
  chartType: ChartType,
  t: TFunction
): ChartData[] {
  const ChartDataList: ChartData[] = [];
  for (let x = 0; x < scheduleList.length; x += 1) {
    const ganttChartProcessList: GanttChartProcess[] = [];

    const chartData: ChartData = {
      ProductionId: scheduleList[x].production_id,
      production_no: scheduleList[x].production_no,
      modelName: scheduleList[x].model_name,
      salesMy: scheduleList[x].my,
      processList: ganttChartProcessList,
      isChild: false,
    };

    const step1: ProcessData[] = [];
    const step2: ProcessData[] = [];
    makeGanttChartItem(step1, step2, ganttChartProcessList, x, scheduleList[x]);
    ChartDataList.push(chartData);

    const childList = scheduleList[x].children;
    if (childList !== undefined) {
      const cntChildList = childList?.length ?? 0;
      for (let z = 0; z < cntChildList; z += 1) {
        const ganttChartChildProcessList: GanttChartProcess[] = [];
        const childChartData: ChartData = {
          ProductionId: childList[z].production_id,
          production_no: childList[z].production_no,
          modelName: childList[z].model_name,
          salesMy: childList[z].my,
          processList: ganttChartChildProcessList,
          isChild: true,
          parent_production_no: scheduleList[x].production_no,
        };
        const childStep1: ProcessData[] = [];
        const childStep2: ProcessData[] = [];
        makeGanttChartItem(childStep1, childStep2, ganttChartChildProcessList, x, childList[z]);
        ChartDataList.push(childChartData);
      }
    }
  }
  return ChartDataList;

  function makeGanttChartItem(
    step1: ProcessData[],
    step2: ProcessData[],
    ganttChartProcessList: GanttChartProcess[],
    x: number,
    targetScheduleList: ScheduleListItem
  ) {
    let tempDate = -1;
    let isNewStep = true;
    for (let y = 0; y < dateList.length; y += 1) {
      const preMonday = previousMonday(dateList[y]);
      let weekNum = 0;
      if (dateList[y].getDay() === 1) {
        weekNum = Math.floor((dateList[y].getDate() + 6) / 7);
      } else {
        weekNum = Math.floor((preMonday.getDate() + 6) / 7);
      }
      switch (chartType) {
        case 'Month':
          if (tempDate !== dateList[y].getMonth()) {
            tempDate = dateList[y].getMonth();
            isNewStep = true;
          }
          break;
        case 'Week':
          if (tempDate !== weekNum) {
            tempDate = weekNum;
            isNewStep = true;
          }
          break;
        case 'Day':
          isNewStep = true;
          break;
        default:
          break;
      }

      if (isNewStep) {
        const newStep1 = _.cloneDeep(step1);
        const newStep2 = _.cloneDeep(step2);
        const item: GanttChartProcess = {
          step1: newStep1,
          step2: newStep2,
        };
        ganttChartProcessList.push(item);
        isNewStep = false;
      }

      // 日付を「yyyy/MM/dd」の形式で取得し、「yyyy-MM-dd」に変換する
      const fromDate = getFormattedDate(dateList[y], 'yyyy-MM-dd');

      if (targetScheduleList.preproduction.plan_from_on === fromDate) {
        const type = '制作前';
        const period = GetPeriod(
          targetScheduleList.preproduction.plan_from_on ?? '',
          targetScheduleList.preproduction.plan_end_on ?? ''
        );
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, type, 'plan', t('制作前'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step1?.push(item);
        }
      }
      if (targetScheduleList.g1?.plan_from_on === fromDate) {
        const period = GetPeriod(targetScheduleList.g1?.plan_from_on ?? '', targetScheduleList.g1?.plan_end_on ?? '');
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G1', 'plan', t('G1'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step1?.push(item);
        }
      }

      if (targetScheduleList.g2?.plan_from_on === fromDate) {
        const period = GetPeriod(targetScheduleList.g2?.plan_from_on ?? '', targetScheduleList.g2?.plan_end_on ?? '');
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G2', 'plan', t('G2'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step1?.push(item);
        }
      }

      if (targetScheduleList.g3?.plan_from_on === fromDate) {
        const period = GetPeriod(targetScheduleList.g3?.plan_from_on ?? '', targetScheduleList.g3?.plan_end_on ?? '');
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G3', 'plan', t('G3'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step1?.push(item);
        }
      }

      if (targetScheduleList.g4?.plan_from_on === fromDate) {
        const period = GetPeriod(targetScheduleList.g4?.plan_from_on ?? '', targetScheduleList.g4?.plan_end_on ?? '');
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G4', 'plan', t('G4'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step1?.push(item);
        }
      }
      if (targetScheduleList.preproduction?.actual_from_on === fromDate) {
        const period = GetPeriod(
          targetScheduleList.preproduction?.actual_from_on ?? '',
          targetScheduleList.preproduction?.actual_end_on ?? ''
        );
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, '制作前', 'actual', t('制作前'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step2?.push(item);
        }
      }
      if (targetScheduleList.g1?.actual_from_on === fromDate) {
        const period = GetPeriod(
          targetScheduleList.g1?.actual_from_on ?? '',
          targetScheduleList.g1?.actual_end_on ?? ''
        );
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G1', 'actual', t('G1'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step2?.push(item);
        }
      }

      if (targetScheduleList.g2?.actual_from_on === fromDate) {
        const period = GetPeriod(
          targetScheduleList.g2?.actual_from_on ?? '',
          targetScheduleList.g2?.actual_end_on ?? ''
        );
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G2', 'actual', t('G2'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step2?.push(item);
        }
      }

      if (targetScheduleList.g3?.actual_from_on === fromDate) {
        const period = GetPeriod(
          targetScheduleList.g3?.actual_from_on ?? '',
          targetScheduleList.g3?.actual_end_on ?? ''
        );
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G3', 'actual', t('G3'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step2?.push(item);
        }
      }

      if (targetScheduleList.g4?.actual_from_on === fromDate) {
        const period = GetPeriod(
          targetScheduleList.g4?.actual_from_on ?? '',
          targetScheduleList.g4?.actual_end_on ?? ''
        );
        // item作成
        const item: ProcessData | null = getItem(fromDate, period, 'G4', 'actual', t('G4'));
        if (item !== null) {
          ganttChartProcessList[ganttChartProcessList.length - 1].step2?.push(item);
        }
      }
    }
  }

  /**
   * ガントチャートの1要素を作成する
   * @param fromDate
   * @param period
   * @param type
   * @param schedulePlan
   * @param name
   * @returns
   */
  function getItem(fromDate: string, period: number, type: string, schedulePlan: string, name: string) {
    if (period <= 0) {
      return null;
    }
    const start = new Date(fromDate);
    let color = '';
    if (schedulePlan === 'plan' && type === 'G2') {
      color = 'pale-r';
    } else if (schedulePlan === 'plan' && type !== 'G2') {
      color = 'pale-g';
    } else if (schedulePlan === 'actual' && type === 'G2') {
      color = 'dark-r';
    } else {
      color = 'dark-g';
    }
    const item: ProcessData = {
      name,
      color,
      paddingLeft: GetPaddingLeftByChartType(start, period, chartType),
      period,
      width: GetWidthByChartType(start, period, chartType),
      fromDate,
    };
    return item;
  }
}

/**
 * すべての日程データの中から期間情報を生成
 * @param data
 * @returns
 */
function getDateList(data: ScheduleListItem[]) {
  let startDay = new Date(2999, 12, 31);
  let endDay = new Date(0, 0, 0);
  for (let i = 0; i < data.length; i += 1) {
    compareDate(data[i].preproduction.plan_from_on);
    compareDate(data[i].preproduction.plan_end_on);
    compareDate(data[i].preproduction.actual_from_on);
    compareDate(data[i].preproduction.actual_end_on);
    compareDate(data[i].g1.plan_from_on);
    compareDate(data[i].g1.plan_end_on);
    compareDate(data[i].g1.actual_from_on);
    compareDate(data[i].g1.actual_end_on);
    compareDate(data[i].g2.plan_from_on);
    compareDate(data[i].g2.plan_end_on);
    compareDate(data[i].g2.actual_from_on);
    compareDate(data[i].g2.actual_end_on);
    compareDate(data[i].g3.plan_from_on);
    compareDate(data[i].g3.plan_end_on);
    compareDate(data[i].g3.actual_from_on);
    compareDate(data[i].g3.actual_end_on);
    compareDate(data[i].g4.plan_from_on);
    compareDate(data[i].g4.plan_end_on);
    compareDate(data[i].g4.actual_from_on);
    compareDate(data[i].g4.actual_end_on);
    const { children } = data[i];
    if (children !== undefined) {
      for (let n = 0; n < children.length ?? 0; n += 1) {
        compareDate(children[n].preproduction.plan_from_on);
        compareDate(children[n].preproduction.plan_from_on);
        compareDate(children[n].preproduction.plan_end_on);
        compareDate(children[n].preproduction.actual_from_on);
        compareDate(children[n].preproduction.actual_end_on);
        compareDate(children[n].g1.plan_from_on);
        compareDate(children[n].g1.plan_end_on);
        compareDate(children[n].g1.actual_from_on);
        compareDate(children[n].g1.actual_end_on);
        compareDate(children[n].g2.plan_from_on);
        compareDate(children[n].g2.plan_end_on);
        compareDate(children[n].g2.actual_from_on);
        compareDate(children[n].g2.actual_end_on);
        compareDate(children[n].g3.plan_from_on);
        compareDate(children[n].g3.plan_end_on);
        compareDate(children[n].g3.actual_from_on);
        compareDate(children[n].g3.actual_end_on);
        compareDate(children[n].g4.plan_from_on);
        compareDate(children[n].g4.plan_end_on);
        compareDate(children[n].g4.actual_from_on);
        compareDate(children[n].g4.actual_end_on);
      }
    }
  }

  return { startDay, endDay };

  function compareDate(dateStr: string | undefined) {
    const tempDate = new Date(dateStr ?? NaN);
    if (!Number.isNaN(tempDate.getTime()) && startDay > tempDate) {
      startDay = tempDate;
    }
    if (!Number.isNaN(tempDate.getTime()) && endDay < tempDate) {
      endDay = tempDate;
    }
  }
}

/**
 * 子日程の管理Noを取得
 */
function getChildProductionIdList(data: ScheduleListItem[]) {
  const productionIdList = [];
  for (let i = 0; i < data.length; i += 1) {
    const { children } = data[i];
    if (children !== null && children !== undefined) {
      productionIdList.push(data[i].production_no);
    }
  }
  return productionIdList;
}

/**
 * チャート種別ごとにwidthを計算する
 * @param startDate
 * @param period
 * @param chartType
 */
function GetWidthByChartType(startDate: Date, period: number, chartType: ChartType) {
  const CELL_WIDTH = 84;

  // 日付の場合
  if (chartType === 'Day') {
    return period * CELL_WIDTH;
  }

  // 週の場合
  if (chartType === 'Week') {
    return (CELL_WIDTH * period) / 7;
  }

  // 月の場合
  // 終了日
  const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + period);

  // スタート日付の月末日
  const targetMonthLastDay = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

  // 同年同月の場合
  if (startDate.getFullYear() === endDate.getFullYear() && startDate.getMonth() === endDate.getMonth()) {
    return (CELL_WIDTH * period) / targetMonthLastDay.getDate();
  }

  // 何月分離れているか計算
  const monthPeriod =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth();

  // 最初の月範囲の長さ
  const startMonthWidth =
    (CELL_WIDTH * (targetMonthLastDay.getDate() - startDate.getDate() + 1)) / targetMonthLastDay.getDate();

  // 終了日付の終了日付取得
  const endMonthLastDay = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
  // 終了月範囲の長さ
  const endMonthWidth = (CELL_WIDTH * endDate.getDate()) / endMonthLastDay.getDate();

  // 3ヶ月以上ある場合の中間月の長さ
  const middleMonthWidth = CELL_WIDTH * (monthPeriod - 1);

  return startMonthWidth + middleMonthWidth + endMonthWidth;
}

/**
 * チャート種別ごとにPaddingLeftを計算する
 * @param startDate
 * @param period
 * @param chartType
 */
function GetPaddingLeftByChartType(startDate: Date, period: number, chartType: ChartType) {
  // 日の場合
  if (chartType === 'Day') {
    return 0;
  }
  // 月の場合
  if (chartType === 'Month') {
    if (startDate.getDate() === 1) {
      return 0;
    }
    // 開始月の最終日
    const targetMonthLastDay = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
    return ((startDate.getDate() - 1) * 84) / targetMonthLastDay.getDate();
  }

  // 週の場合
  // 日曜:0 月曜:1
  let dayByWeek = startDate.getDay();
  if (dayByWeek === 0) {
    dayByWeek = 6;
    return (dayByWeek * 84) / 7;
  }
  if (dayByWeek === 1) {
    dayByWeek = 0;
    return 0;
  }
  dayByWeek -= 1;
  return (dayByWeek * 84) / 7;
}

/**
 * 日付期間を取得
 * @param startDateStr
 * @param endDateStr
 * @returns
 */
function GetPeriod(startDateStr: string, endDateStr: string) {
  if (startDateStr === '' || endDateStr === '') {
    return 0;
  }
  const startDate: Date = new Date(startDateStr);
  const endDate: Date = new Date(endDateStr);
  const diffDay: number = Math.floor((endDate.getTime() - startDate.getTime()) / 86400000);
  return diffDay + 1;
}

export default GanttChartArea;
